// dependencies
import { useAutoAnimate } from "@formkit/auto-animate/react";
import React, { PropsWithChildren } from "react";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Alert } from "@performyard/py-components";

import Link from "../../../components/Link";

import { ColorShade } from "../../../app/Theme";
import Spacer from "../../Spacer";
// components
import Text, { TextSize, textWeights } from "../../Text";
import Tooltip from "../../Tooltip";
// styles
import css from "./InputWrapper.module.scss";

export interface Info {
	title?: string;
	message: string;
	titleBottomLine?: boolean;
}

export interface InputWrapperProps {
	/**
	 * The label to display above the input
	 */
	label?: string;
	/**
	 * The label text size to display above the input
	 */
	labelSize?: TextSize;
	/**
	 * The label text size to display above the input
	 */
	labelWeight?: (typeof textWeights)[number];
	/**
	 * Displays an info icon next to the label that shows more info when hovered
	 */
	info?: Info;
	/**
	 * Additional styling for InputWrapper
	 */
	style?: object;
	/**
	 * Color of the label
	 */
	color?: ColorShade;
	/**
	 * If disabled, update the wrapper with correct styling
	 */
	disabled?: boolean;
	/**
	 * Displays the error message below the input
	 */
	errorMessage?: string;
	/**
	 * Id of the input
	 */
	inputId?: string;
	/**
	 * should the error message be animated
	 */
	animatedError?: boolean;

	/**
	 * Image renderd to the right of the title
	 */
	titleImage?: string;

	/**
	 *	href of the title
	 */
	titleHref?: string;

	/**
	 * title tooltip
	 */
	titleTooltip?: string;
}

/**
 * @deprecated Use py-component input wrapper instead
 * Wrapper component for inputs that allows you to display a label, info icon, and error message
 */
const InputWrapper = (props: PropsWithChildren<InputWrapperProps>) => {
	const {
		label = "",
		labelSize = "paragraph-3",
		labelWeight = "semi-bold",
		info,
		style,
		color = null,
		disabled,
		errorMessage,
		inputId = "",
		children,
		animatedError = false,
		titleImage,
		titleHref,
		titleTooltip,
	} = props;

	const [animateRef, enableAnimations] = useAutoAnimate();
	enableAnimations(animatedError);

	const getLabelColor = () => {
		if (errorMessage) {
			return "red-6";
		}
		if (disabled) {
			return "neutral-4";
		}
		return color;
	};

	const getLabel = () => {
		if (titleHref) {
			return (
				<Tooltip message={titleTooltip || ""} disabled={!titleTooltip} placement="top">
					<Link noUnderline href={titleHref}>
						<Text
							id={`label-${inputId}`}
							className={css.labelLink}
							color={getLabelColor() || "neutral-9"}
							element="label"
							size={labelSize}
							weight={labelWeight}
							htmlFor={inputId}
						>
							{`${label}${errorMessage ? "*" : ""}`}
						</Text>
					</Link>
				</Tooltip>
			);
		} else {
			return (
				<Text
					id={`label-${inputId}`}
					color={getLabelColor() || undefined}
					element="label"
					size={labelSize}
					weight={labelWeight}
					htmlFor={inputId}
				>
					{`${label}${errorMessage ? "*" : ""}`}
				</Text>
			);
		}
	};

	return (
		<div style={style} ref={animateRef}>
			<span className={css.label}>
				{label ? getLabel() : null}
				{titleImage ? <img src={titleImage} alt="titleImage" className={css.titleImage} /> : null}
				{info ? (
					<span className={css.infoIcon}>
						<Tooltip
							title={info.title}
							message={info.message}
							titleBottomLine={info.titleBottomLine}
							placement="right"
						>
							<FontAwesomeIcon icon={faCircleInfo} data-testid="circle-info" />
						</Tooltip>
					</span>
				) : null}
			</span>
			{label ? <Spacer height={1} /> : null}
			{children}
			{errorMessage ? (
				<Alert
					type="error"
					message={errorMessage}
					items="start"
					dangerouslySetClassName={css.errorMessage}
					weight="semi-bold"
				/>
			) : null}
		</div>
	);
};

/** @deprecated Use py-component input wrapper instead */
export default InputWrapper;
